import React, { useCallback, useState } from 'react';

import type { TransitionStatus } from 'react-transition-state';

import { Avatar, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core/react/i18n';

import type { HeroFeedItem } from 'src/app/pages/promote/pageContext';

import { useHeroFeedItemPill } from './hooks/useHeroFeedItemPill';

type Props = {
	item: HeroFeedItem;
	animateDelay: number;
	animateSpeed: number;
	status: TransitionStatus;
	role?: React.AriaRole;
};

export const HeroFeedItemPill: React.FC<Props> = ({ item, animateDelay, animateSpeed, status, role }) => {
	const {
		data: { amountFormatted, avatarUrl },
	} = useHeroFeedItemPill(item);

	const [offsetWidth, setOffsetWidth] = useState(250);

	const rootDiv = useCallback((node: HTMLDivElement | null) => {
		if (node) {
			setOffsetWidth(node.offsetWidth);
		}
	}, []);

	const slideToLeftTransitionStyles: React.CSSProperties = {
		transition: `margin-left ${animateSpeed}ms cubic-bezier(0.4, 0.45, 0.35, 1), opacity ${animateDelay + animateSpeed}ms`,
		...(status === 'exiting' ? { marginLeft: -offsetWidth } : undefined),
	};

	return (
		<div
			ref={rootDiv}
			role={role}
			className={cn(
				'mr-2 inline-flex items-center gap-2 rounded-full bg-gray-100 p-1.5 pr-3',
				status === 'exiting' && 'opacity-0',
			)}
			style={slideToLeftTransitionStyles}
		>
			<Avatar alt="" src={avatarUrl} size="small" />

			<div className="flex flex-1 items-center gap-1">
				<Text fontWeight="bold" variant="body" size="x-small" color="strong">
					{item.userDisplayName}
				</Text>
				<Text variant="body" size="x-small" color="strong">
					<Translate
						value="fe.pages.sponsors.promote_feed.chipped_in_text"
						replacements={{ amount_formatted: amountFormatted }}
					/>
				</Text>
			</div>
		</div>
	);
};
